import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import images from "../../../../utilities/images/images";
import completedSetIcon from "../../../../utilities/images/adaptive_qbank/completed_set.png";
import {
  postAdaptiveUserAnalysisRequest,
  fetchAdaptiveSolutionRequest,
} from "../../../../redux/adaptive_qbank/adaptiveQBankSlice";
import AdaptiveUpgradeModal from "../components/AdaptiveUpgradeModal";
import QuestionGridNumber from "../../../analytics/components/QuestionWise/QuestionGridNumber";
import "./AdaptiveSubmit.css";

const getRoot = (payload) => payload?.data ?? payload ?? {};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (Array.isArray(value?.list)) return value.list;
  if (Array.isArray(value?.questions)) return value.questions;
  if (Array.isArray(value?.allquestionlist)) return value.allquestionlist;
  if (Array.isArray(value?.question_list)) return value.question_list;
  if (Array.isArray(value?.question_data)) return value.question_data;
  if (Array.isArray(value?.result)) return value.result;
  return [];
};

const pickValue = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
};

const AQB_DEFAULT_PAPER_TYPE = 20;
const QUESTIONS_PER_SECTION = 5;
const TOTAL_SUMMARY_QUESTIONS = 10;

const hasAttemptedAnswer = (q = {}) => {
  const selectedOptionRaw = pickValue(
    q?.selectedOption,
    q?.selected_option,
    q?.user_selected_option,
    q?.selected_option_id,
    q?.user_option_id
  );
  const selectedOptionIds = Array.isArray(selectedOptionRaw)
    ? selectedOptionRaw
    : selectedOptionRaw !== null &&
        selectedOptionRaw !== undefined &&
        String(selectedOptionRaw).trim() !== ""
      ? [selectedOptionRaw]
      : [];

  const integerAnswer = String(
    pickValue(q?.integerAnswer, q?.integer_Answer, q?.integer_answer, q?.option_int, "")
  ).trim();

  return selectedOptionIds.length > 0 || integerAnswer !== "";
};

const isQuestionCorrect = (q = {}) => {
  const directFlag = pickValue(
    q?.is_correct,
    q?.isCorrect,
    q?.correct,
    q?.user_answered_correct,
    q?.is_user_correct
  );
  if (directFlag === true || Number(directFlag) === 1) return true;
  if (directFlag === false || Number(directFlag) === 0) return false;

  const statusText = String(
    pickValue(q?.answer_status, q?.result, q?.user_result, q?.status_text, "")
  ).toLowerCase();
  if (statusText.includes("correct")) return true;
  if (statusText.includes("wrong") || statusText.includes("incorrect")) return false;

  const answerType = Number(q?.answer_type_id);
  if (answerType === 2 && Array.isArray(q?.option)) {
    const selectedRaw = pickValue(
      q?.selectedOption,
      q?.selected_option,
      q?.user_selected_option,
      q?.selected_option_id,
      q?.user_option_id
    );
    const selectedIds = Array.isArray(selectedRaw)
      ? selectedRaw.map((id) => String(id))
      : selectedRaw !== null && selectedRaw !== undefined && String(selectedRaw).trim() !== ""
        ? [String(selectedRaw)]
        : [];
    const correctIds = q.option
      .filter((opt) => Number(opt?.is_correct) === 1)
      .map((opt) => String(opt?.option_id));

    if (!selectedIds.length || !correctIds.length) return false;
    const selectedSet = new Set(selectedIds);
    const correctSet = new Set(correctIds);
    if (selectedSet.size !== correctSet.size) return false;
    return [...correctSet].every((id) => selectedSet.has(id));
  }

  if (answerType === 1 && Array.isArray(q?.option)) {
    const selectedId = String(
      pickValue(
        q?.selectedOption,
        q?.selected_option,
        q?.user_selected_option,
        q?.selected_option_id,
        q?.user_option_id,
        ""
      )
    ).trim();
    if (!selectedId) return false;
    return q.option.some(
      (opt) =>
        String(opt?.option_id) === selectedId && Number(opt?.is_correct) === 1
    );
  }

  if (answerType === 3) {
    const userAnswer = String(
      pickValue(q?.integerAnswer, q?.integer_Answer, q?.integer_answer, q?.option_int, "")
    ).trim();
    const correctAnswer = String(
      pickValue(q?.answer, q?.correct_answer, q?.answer_text, "")
    ).trim();
    if (!userAnswer || !correctAnswer) return false;

    const userNum = Number(userAnswer);
    const correctNum = Number(correctAnswer);
    if (Number.isFinite(userNum) && Number.isFinite(correctNum)) {
      return userNum === correctNum;
    }
    return userAnswer === correctAnswer;
  }

  return false;
};

const isQuestionWrong = (q = {}) => {
  if (!hasAttemptedAnswer(q)) return false;
  if (isQuestionCorrect(q)) return false;
  const wrongFlag = pickValue(q?.is_wrong, q?.isWrong, q?.incorrect);
  if (wrongFlag === true || Number(wrongFlag) === 1) return true;

  const statusText = String(
    pickValue(q?.answer_status, q?.result, q?.user_result, "")
  ).toLowerCase();
  if (statusText.includes("wrong") || statusText.includes("incorrect")) return true;

  return true;
};

const getQuestionBucketLabel = (q = {}, fallbackIndex = 0) =>
  pickValue(
    q?.subtopic_name,
    q?.sub_topic_name,
    q?.subTopicName,
    q?.topic_name,
    q?.topicName,
    q?.section_name,
    q?.sectionName,
    `Section ${fallbackIndex + 1}`
  );

const getQuestionDifficulty = (q = {}) => {
  const raw = String(
    pickValue(
      q?.difficulty,
      q?.difficulty_level,
      q?.difficultyLevel,
      q?.level,
      q?.current_difficulty,
      "Medium"
    )
  ).toLowerCase();

  if (raw.includes("hard")) return "Hard";
  if (raw.includes("easy")) return "Easy";
  return "Medium";
};

const getCardTone = (statusTag = "", score = 0, total = 0) => {
  const status = String(statusTag).toLowerCase();
  if (status.includes("fail")) {
    return "danger";
  }
  if (
    status.includes("crack") ||
    status.includes("master") ||
    status.includes("pass") ||
    (Number(total) > 0 && Number(score) / Number(total) >= 0.6)
  ) {
    return "success";
  }
  return "danger";
};

const isCrackedStatus = (statusTag = "") =>
  String(statusTag).toLowerCase().includes("crack");
const isFailedStatus = (statusTag = "") =>
  String(statusTag).toLowerCase().includes("fail");

const resolveAdaptiveIdentity = ({ routeTest, plannerDetails, testDetails, submitRoot }) => {
  const adaptiveQuestionId = pickValue(
    routeTest?.adaptive_question_id,
    routeTest?.adaptiveQuestionId,
    routeTest?.aqb_id,
    routeTest?.id,
    routeTest?.paper_id,
    submitRoot?.adaptive_question_id,
    testDetails?.adaptive_question_id,
    testDetails?.testId,
    testDetails?.paperId,
    plannerDetails?.planner_test_id
  );

  const paperType = Number(
    pickValue(
    routeTest?.paper_type,
    routeTest?.paperType,
    routeTest?.category_id,
    testDetails?.paperType,
    plannerDetails?.id,
    testDetails?.format,
    AQB_DEFAULT_PAPER_TYPE
    )
  );

  return {
    adaptive_question_id: adaptiveQuestionId || null,
    paper_type: paperType,
  };
};

const AdaptiveSubmit = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const routeState = location?.state ?? {};
  const routeTest = routeState?.test || routeState?.item || routeState;

  const { submitResult, userAnalysis, allQuestionList, solution, loading, error } = useSelector(
    (s) => s.adaptiveQBank
  );
  const { plannerDetails, testDetails } = useSelector((s) => s.examSlice);

  const routeFocusSetNo = Number(
    pickValue(
      routeState?.focusSetNo,
      routeTest?.focus_set_no,
      routeTest?.focusSetNo,
      1
    )
  );
  const routeAdaptiveQuestionId = pickValue(
    routeState?.adaptive_question_id,
    routeState?.adaptiveQuestionId,
    routeTest?.adaptive_question_id,
    routeTest?.adaptiveQuestionId
  );
  const submitRoot = useMemo(() => getRoot(submitResult), [submitResult]);
  const userAnalysisRoot = useMemo(() => getRoot(userAnalysis), [userAnalysis]);
  const summaryRoot = useMemo(() => {
    const analysisFocusSetNo = Number(
      pickValue(userAnalysisRoot?.focus_set_no, userAnalysisRoot?.focusSetNo, 0)
    );
    const analysisAdaptiveId = pickValue(
      userAnalysisRoot?.adaptive_question_id,
      userAnalysisRoot?.adaptiveQuestionId
    );

    const hasRouteFocusSet = Number.isFinite(routeFocusSetNo) && routeFocusSetNo > 0;
    const hasAnalysisFocusSet = Number.isFinite(analysisFocusSetNo) && analysisFocusSetNo > 0;
    const focusSetMatches = !hasRouteFocusSet || !hasAnalysisFocusSet
      ? true
      : analysisFocusSetNo === routeFocusSetNo;

    const hasRouteAdaptiveId =
      routeAdaptiveQuestionId !== undefined &&
      routeAdaptiveQuestionId !== null &&
      String(routeAdaptiveQuestionId).trim() !== "";
    const hasAnalysisAdaptiveId =
      analysisAdaptiveId !== undefined &&
      analysisAdaptiveId !== null &&
      String(analysisAdaptiveId).trim() !== "";
    const adaptiveIdMatches = !hasRouteAdaptiveId || !hasAnalysisAdaptiveId
      ? true
      : String(analysisAdaptiveId) === String(routeAdaptiveQuestionId);

    if (userAnalysis && focusSetMatches && adaptiveIdMatches) {
      return userAnalysisRoot;
    }
    return submitRoot;
  }, [routeAdaptiveQuestionId, routeFocusSetNo, submitRoot, userAnalysis, userAnalysisRoot]);
  const questionList = useMemo(() => toArray(getRoot(allQuestionList)), [allQuestionList]);
  const adaptiveIdentity = useMemo(
    () =>
      resolveAdaptiveIdentity({
        routeTest,
        plannerDetails,
        testDetails,
        submitRoot,
      }),
    [plannerDetails, routeTest, submitRoot, testDetails]
  );

  useEffect(() => {
    if (!adaptiveIdentity.adaptive_question_id) return;

    dispatch(
      postAdaptiveUserAnalysisRequest({
        adaptive_question_id: adaptiveIdentity.adaptive_question_id,
      })
    );
  }, [adaptiveIdentity.adaptive_question_id, dispatch]);

  useEffect(() => {
    if (!adaptiveIdentity.adaptive_question_id) return;
    const paperType = Number(
      pickValue(adaptiveIdentity.paper_type, AQB_DEFAULT_PAPER_TYPE) || AQB_DEFAULT_PAPER_TYPE
    );
    if (!Number.isFinite(paperType) || paperType <= 0) return;

    dispatch(
      fetchAdaptiveSolutionRequest({
        paper_id: adaptiveIdentity.adaptive_question_id,
        paper_type: paperType,
      })
    );
  }, [adaptiveIdentity.adaptive_question_id, adaptiveIdentity.paper_type, dispatch]);

  const summaryCards = useMemo(() => {
    const analysisCards = toArray(
      pickValue(
        summaryRoot?.performance_cards,
        summaryRoot?.performanceCards,
        summaryRoot?.cards
      )
    );

    if (analysisCards.length > 0) {
      return analysisCards.slice(0, 2).map((card, idx) => {
        const score = Number(
          pickValue(
            card?.correct,
            card?.correct_count,
            card?.score,
            card?.right,
            0
          )
        );
        const total = Number(
          pickValue(
            card?.total,
            card?.total_questions,
            card?.question_count,
            card?.questions,
            QUESTIONS_PER_SECTION
          )
        );
        const normalizedScore = Number.isFinite(score) ? Math.max(0, score) : 0;
        const normalizedTotal = QUESTIONS_PER_SECTION;
        const safeScore = Math.min(
          normalizedScore,
          Number.isFinite(total) && total > 0 ? total : normalizedTotal,
          normalizedTotal
        );
        const isCracked = safeScore > 3;

        return {
          id: idx,
          title: pickValue(
            card?.title,
            card?.subtopic_name,
            card?.sub_topic_name,
            card?.name,
            `Section ${idx + 1}`
          ),
          score: safeScore,
          total: normalizedTotal,
          subtitle: isCracked ? "Mastered" : "Needs Practice",
          statusTag: isCracked ? "Cracked" : "Failed",
          difficulty: pickValue(
            card?.difficulty,
            card?.difficulty_level,
            card?.level,
            "Medium"
          ),
          tone: isCracked ? "success" : "danger",
        };
      });
    }

    const firstTen = questionList.slice(0, TOTAL_SUMMARY_QUESTIONS);
    const sections = [
      firstTen.slice(0, QUESTIONS_PER_SECTION),
      firstTen.slice(QUESTIONS_PER_SECTION, TOTAL_SUMMARY_QUESTIONS),
    ].filter((section) => section.length > 0);

    return sections.map((sectionQuestions, idx) => {
      const score = sectionQuestions.filter((q) => isQuestionCorrect(q)).length;
      const total = sectionQuestions.length || QUESTIONS_PER_SECTION;
      const subtitle = score > 3 ? "Mastered" : "Needs Practice";
      const statusTag = score > 3 ? "Cracked" : "Failed";

      return {
        id: idx,
        title: getQuestionBucketLabel(sectionQuestions[0], idx),
        score,
        total,
        subtitle,
        statusTag,
        difficulty: getQuestionDifficulty(sectionQuestions[0]),
        tone: score > 3 ? "success" : "danger",
      };
    });
  }, [questionList, summaryRoot]);

  const focusSetNo = Number(
    pickValue(
      routeState?.focusSetNo,
      routeTest?.focus_set_no,
      routeTest?.focusSetNo,
      summaryRoot?.focus_set_no,
      summaryRoot?.focusSetNo,
      1
    )
  );

  const focusSetTitle = pickValue(
    summaryRoot?.focus_set_title,
    summaryRoot?.focus_set_name,
    Number.isFinite(focusSetNo) ? `Focus Set ${focusSetNo}` : null,
    routeTest?.test_name,
    routeTest?.paper_name,
    "Focus Set"
  );
  const isFreeUser = summaryRoot?.is_user_free === true || Number(summaryRoot?.is_user_free) === 1;
  const revisitQuestions = useMemo(() => {
    const summaryNode = pickValue(summaryRoot?.summary, summaryRoot?.overall, {});
    const expectedWrongCount = Number(
      pickValue(summaryNode?.wrong, summaryNode?.incorrect, summaryRoot?.wrong, 0)
    );
    const hasAnalysisRevisitSource =
      summaryRoot?.questions_to_revisit !== undefined ||
      summaryRoot?.questionsToRevisit !== undefined;
    const revisitFromAnalysis = toArray(
      pickValue(
        summaryRoot?.questions_to_revisit?.list,
        summaryRoot?.questionsToRevisit?.list,
        summaryRoot?.questions_to_revisit,
        summaryRoot?.questionsToRevisit
      )
    );

    if (revisitFromAnalysis.length > 0) {
      const normalizedAnalysisRevisit = revisitFromAnalysis.map((q, idx) => ({
        id: pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1),
        text: pickValue(
          q?.question,
          q?.question_text,
          q?.text,
          `Question ID ${pickValue(q?.question_id, q?.qid, q?.id, idx + 1)}`
        ),
      }));
      return Number.isFinite(expectedWrongCount) && expectedWrongCount >= 0
        ? normalizedAnalysisRevisit.slice(0, expectedWrongCount)
        : normalizedAnalysisRevisit;
    }

    // If backend explicitly provides revisit payload (including empty list),
    // trust it and do not derive fallback revisit questions from questionList.
    if (hasAnalysisRevisitSource) {
      return [];
    }

    const wrongFromQuestionList = questionList
      .slice(0, TOTAL_SUMMARY_QUESTIONS)
      .filter((q) => isQuestionWrong(q))
      .map((q, idx) => ({
        id: pickValue(q?.q_no, q?.question_no, q?.display_number, idx + 1),
        text: pickValue(
          q?.question,
          q?.question_text,
          q?.text,
          `Question ID ${pickValue(q?.question_id, q?.qid, q?.id, idx + 1)}`
        ),
      }));

    if (Number.isFinite(expectedWrongCount) && expectedWrongCount > 0) {
      return wrongFromQuestionList.slice(0, expectedWrongCount);
    }
    return wrongFromQuestionList;
  }, [questionList, summaryRoot]);

  const revisitDebug = useMemo(() => {
    const summaryNode = pickValue(summaryRoot?.summary, summaryRoot?.overall, {});
    const expectedWrongCount = Number(
      pickValue(summaryNode?.wrong, summaryNode?.incorrect, summaryRoot?.wrong, 0)
    );
    const analysisList = toArray(
      pickValue(
        summaryRoot?.questions_to_revisit?.list,
        summaryRoot?.questionsToRevisit?.list,
        summaryRoot?.questions_to_revisit,
        summaryRoot?.questionsToRevisit
      )
    );
    const topTen = questionList.slice(0, TOTAL_SUMMARY_QUESTIONS);
    const fallbackWrong = topTen.filter((q) => isQuestionWrong(q));
    const attemptedTopTen = topTen.filter((q) => hasAttemptedAnswer(q));

    return {
      focusSetNo,
      expectedWrongCount: Number.isFinite(expectedWrongCount) ? expectedWrongCount : 0,
      analysisListCount: analysisList.length,
      attemptedTopTenCount: attemptedTopTen.length,
      fallbackWrongCount: fallbackWrong.length,
      finalRevisitCount: revisitQuestions.length,
      // Keep sample tiny so console stays readable.
      analysisSample: analysisList.slice(0, 3).map((q) => ({
        q_no: pickValue(q?.q_no, q?.question_no, q?.display_number),
        question_id: pickValue(q?.question_id, q?.qid, q?.id),
        answer_status: q?.answer_status,
        result: q?.result,
        is_wrong: q?.is_wrong,
        incorrect: q?.incorrect,
      })),
    };
  }, [focusSetNo, questionList, revisitQuestions, summaryRoot]);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    // Diagnostic logs to trace why revisit count differs from attempted/wrong stats.
    console.groupCollapsed(
      `[AQB Submit Debug] Focus Set ${revisitDebug.focusSetNo} revisit trace`
    );
    console.log("Expected wrong (summary.wrong):", revisitDebug.expectedWrongCount);
    console.log("Analysis list count:", revisitDebug.analysisListCount);
    console.log("Attempted in top 10:", revisitDebug.attemptedTopTenCount);
    console.log("Wrong from question list fallback:", revisitDebug.fallbackWrongCount);
    console.log("Final rendered revisit count:", revisitDebug.finalRevisitCount);
    console.table(revisitDebug.analysisSample);
    console.groupEnd();
  }, [revisitDebug]);

  const submitGridQuestions = useMemo(() => {
    const baseRows = questionList.slice(0, TOTAL_SUMMARY_QUESTIONS);
    const solutionRoot = getRoot(solution);
    const analyticsRows = Array.isArray(solutionRoot?.data)
      ? solutionRoot.data
      : toArray(solutionRoot);
    if (!baseRows.length) return [];
    if (!analyticsRows.length) return baseRows;

    return baseRows.map((baseRow) => {
      const analyticsRow = analyticsRows.find(
        (q) => String(q?.qid) === String(baseRow?.qid)
      );
      if (!analyticsRow) return baseRow;
      return { ...baseRow, ...analyticsRow };
    });
  }, [questionList, solution]);
  const navigateToNextFocusSet = () => {
    history.push({
      pathname: "/practice/adaptive-question-bank/next-focus-set",
      state: {
        topic: routeState?.topic || routeTest?.topic || null,
        topic_id:
          routeState?.topic?.topic_id ||
          routeState?.topic_id ||
          routeTest?.topic_id,
        topic_name:
          routeState?.topic?.topic_name ||
          routeState?.topic_name ||
          routeTest?.topic_name,
        focusSetNo:
          Number.isFinite(focusSetNo) && focusSetNo > 0
            ? focusSetNo + 1
            : 1,
        completedFocusSetNo:
          Number.isFinite(focusSetNo) && focusSetNo > 0 ? focusSetNo : undefined,
        // Coming from completed submit should not reopen as paused.
        suppressResume: true,
        isPaused: false,
      },
    });
  };

  return (
    <div className="adaptive-submit-page">
      <div className="adaptive-submit-header">
        <img
          src={images.new_backarrow}
          alt="Back"
          className="adaptive-submit-back"
          onClick={navigateToNextFocusSet}
        />
        <h2 className="adaptive-submit-header-title">{focusSetTitle}</h2>
      </div>

      <div className="adaptive-submit-content">
        <section className="adaptive-submit-main">
          {loading?.userAnalysis ? (
            <div className="adaptive-submit-state">Loading analysis...</div>
          ) : null}
          {error ? <div className="adaptive-submit-state error">{error}</div> : null}

          <div className="adaptive-submit-banner">
            <img
              className="adaptive-submit-banner-check"
              src={completedSetIcon}
              alt=""
              aria-hidden="true"
            />
            <h3>{`${focusSetTitle} Completed!`}</h3>
            <p>{summaryRoot?.message || "Great effort! Here's how you performed."}</p>
          </div>

          <div className="adaptive-submit-cards">
            {summaryCards.map((card) => (
              <article key={card.id} className={`adaptive-card ${card.tone}`}>
                <div className="adaptive-card-meta">
                  <span
                    className={`adaptive-card-tag ${
                      isCrackedStatus(card.statusTag)
                        ? "cracked"
                        : isFailedStatus(card.statusTag)
                          ? "failed"
                          : ""
                    }`}
                  >
                    {isCrackedStatus(card.statusTag) ? (
                      <span className="adaptive-card-tag-icon" aria-hidden="true">
                        <img src={images.tick_icon} alt="" />
                      </span>
                    ) : isFailedStatus(card.statusTag) ? (
                      <span className="adaptive-card-tag-icon-failed" aria-hidden="true">
                        <span>×</span>
                      </span>
                    ) : null}
                    {card.statusTag}
                  </span>
                  <span className="adaptive-card-difficulty">{card.difficulty}</span>
                </div>
                <h4>{card.title}</h4>
                <p className="adaptive-card-score">
                  <strong>{card.score}</strong>
                  <span className="adaptive-card-score-total">/{card.total}</span>
                </p>
                <p className="adaptive-card-subtitle">{card.subtitle}</p>
              </article>
            ))}
          </div>

          <div className="adaptive-submit-revisit">
            <h4>{`Questions to Revisit (${revisitQuestions.length})`}</h4>
            <div className="adaptive-submit-revisit-list">
              {revisitQuestions.length === 0 ? (
                <div className="adaptive-submit-empty">No revisit questions found.</div>
              ) : (
                revisitQuestions.map((item) => (
                  <div key={item.id} className="adaptive-submit-revisit-item">
                    <span className="adaptive-submit-revisit-q">{`Q. ${item.id}`}</span>
                    <p>{item.text || "-"}</p>
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="adaptive-submit-actions">
            <button
              type="button"
              className="adaptive-submit-btn secondary"
              onClick={navigateToNextFocusSet}
            >
              Back
            </button>
            <button
              type="button"
              className="adaptive-submit-btn primary"
              onClick={navigateToNextFocusSet}
            >
              Next Focus Set
            </button>
          </div>
        </section>

        <aside className="adaptive-submit-grid-panel">
          {loading?.solution ? (
            <div className="adaptive-submit-grid-loading">Loading response grid…</div>
          ) : (
            <QuestionGridNumber
              selectedIndex={-1}
              questions={submitGridQuestions}
              onSelect={(index) => {
                if (isFreeUser) {
                  setIsUpgradeModalOpen(true);
                  return;
                }
                const selectedQuestion = submitGridQuestions[index];
                if (!selectedQuestion?.qid || !adaptiveIdentity.adaptive_question_id) return;
                history.push("/practice-view-solution", {
                  isAdaptiveQBank: true,
                  paperType: adaptiveIdentity.paper_type || AQB_DEFAULT_PAPER_TYPE,
                  paper_type: adaptiveIdentity.paper_type || AQB_DEFAULT_PAPER_TYPE,
                  paperId: adaptiveIdentity.adaptive_question_id,
                  adaptive_question_id: adaptiveIdentity.adaptive_question_id,
                  selectedQid: selectedQuestion.qid,
                  focusSetNo: Number.isFinite(focusSetNo) ? focusSetNo : routeState?.focusSetNo,
                  topic: routeState?.topic,
                  topic_id: routeState?.topic_id,
                  topic_name: routeState?.topic_name,
                });
              }}
            />
          )}
        </aside>
      </div>
      <AdaptiveUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
      />
    </div>
  );
};

export default AdaptiveSubmit;
